/*
 * <faubourg-product-recommendations>: asks the recommendations endpoint for a
 * section and keeps the products it draws.
 *
 * An asset rather than a snippet's own {% javascript %}: two surfaces hold the
 * element -- the recommendations snippet's row and the complementary block in
 * the buy box -- and each loads this file where it renders. The element wears
 * `hidden` until there is something to show, so a pairing that does not exist
 * leaves no gap. The row asks once the page has loaded, not when it comes into
 * view: the answer takes a third of a second, and a shopper reaching the end
 * of the page in one movement used to get there first. `eager` asks at once,
 * for the buy box, which is already in view. A `data-stand-in` element stays
 * visible when the endpoint has nothing: that is the theme editor, where a
 * placed block must remain something to select.
 */

(function () {
  'use strict';

  class FaubourgProductRecommendations extends HTMLElement {
    controller = null;
    loaded = false;

    connectedCallback() {
      // Moved in the editor or re-attached: what it drew is still drawn.
      if (this.loaded) return;

      if (this.hasAttribute('eager') || document.readyState === 'complete') this.load();
      else window.addEventListener('load', this.handleLoad, { once: true });
    }

    disconnectedCallback() {
      window.removeEventListener('load', this.handleLoad);
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    }

    handleLoad = () => {
      if (this.isConnected && !this.loaded) this.load();
    };

    // An answer that arrives with the shopper at or past its place grows
    // above what they are looking at: what follows it holds where it stands
    // and the page scrolls by what was added. At the top of the page, or with
    // its place below the middle of the screen, it grows downward as a page
    // does. Measured after the change, so whatever the browser's own scroll
    // anchoring already kept still is not kept twice.
    reveal(change) {
      const follower = this.follower();
      if (!follower) {
        change();
        return;
      }
      const before = follower.getBoundingClientRect().top;
      // Where the element starts, which a hidden element has no box to say:
      // there, it starts where what follows it does.
      const place = this.getClientRects().length ? this.getBoundingClientRect().top : before;
      const held = window.scrollY > 0 && place < window.innerHeight / 2;
      change();
      if (!held) return;
      const moved = follower.getBoundingClientRect().top - before;
      if (moved) window.scrollBy({ top: moved, behavior: 'instant' });
    }

    // Nothing to show: the place it held closes, as an arrival would open.
    empty() {
      this.reveal(() => {
        this.hidden = !this.hasAttribute('data-stand-in');
      });
    }

    // The first box after this element's place, which is what holds still.
    follower() {
      for (let node = this; node; node = node.parentElement) {
        for (let next = node.nextElementSibling; next; next = next.nextElementSibling) {
          if (next.getClientRects().length) return next;
        }
      }
      return null;
    }

    // The endpoint renders the stand-in section, whose ids are the same for
    // every block asking, so each block makes the ids of what it drew its own.
    scopeIds(root) {
      const scope = this.dataset.scope;
      if (!scope) return;
      const ids = new Map();
      root.querySelectorAll('[id]').forEach((node) => {
        ids.set(node.id, `${scope}-${node.id}`);
        node.id = ids.get(node.id);
      });
      root.querySelectorAll('[aria-describedby]').forEach((node) => {
        const next = ids.get(node.getAttribute('aria-describedby'));
        if (next) node.setAttribute('aria-describedby', next);
      });
    }

    async load() {
      const controller = new AbortController();
      this.controller = controller;

      try {
        const url = new URL(this.dataset.url, window.location.origin);
        window.Faubourg.carryPageParams(url);

        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(response.statusText);

        const markup = window.Faubourg.parseSection(await response.text());
        const nextList = markup.querySelector('[data-recommendations-list]');
        const list = this.querySelector('[data-recommendations-list]');
        if (!nextList || !nextList.children.length || !list) {
          this.empty();
          this.loaded = true;
          return;
        }

        this.scopeIds(nextList);
        this.reveal(() => {
          list.replaceChildren(...nextList.childNodes);
          this.removeAttribute('data-reserving');
          this.hidden = false;
        });
        this.loaded = true;
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        this.empty();
      } finally {
        this.controller = null;
      }
    }
  }

  if (!customElements.get('faubourg-product-recommendations')) {
    customElements.define('faubourg-product-recommendations', FaubourgProductRecommendations);
  }
})();
